/**
 * Iron Klad storefront interactions.
 *
 * One namespaced module (window.IronKlad) that layers motion, wishlist,
 * recently viewed, carousels, cart extras and small UI helpers on top of
 * Rise's own components. Everything is progressive: without this file the
 * theme still renders and shops normally.
 */
(() => {
  if (window.IronKlad && window.IronKlad.ready) return;

  const IK = (window.IronKlad = window.IronKlad || {});
  const config = window.IronKladConfig || {};
  const strings = config.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const storage = {
    get(key, fallback) {
      try {
        const value = window.localStorage.getItem(key);
        return value ? JSON.parse(value) : fallback;
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* storage full or blocked — feature degrades silently */
      }
    },
  };

  const onPubSub = (eventName, callback) => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return subscribe(PUB_SUB_EVENTS[eventName], callback);
    }
    return () => {};
  };

  const emitCartUpdate = (cartData, source) => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Toast notifications                                                 */
  /* ------------------------------------------------------------------ */
  IK.toast = (message, { type = 'success', timeout = 3200 } = {}) => {
    const region = $('#IkToasts');
    if (!region || !message) return;
    const toast = document.createElement('div');
    toast.className = `ik-toast ik-toast--${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toast.textContent = message;
    region.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      window.setTimeout(() => toast.remove(), 600);
    }, timeout);
  };

  /* ------------------------------------------------------------------ */
  /* Scroll reveal                                                       */
  /* ------------------------------------------------------------------ */
  const reveal = {
    observer: null,
    init(root = document) {
      const elements = $$('[data-ik-reveal]:not(.is-revealed)', root);
      if (!elements.length) return;

      if (reduceMotion.matches || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
        elements.forEach((el) => el.classList.add('is-revealed'));
        return;
      }

      if (!this.observer) {
        // A fully clipped element (clip-path: inset(100%)) never reports as
        // intersecting, so clip reveals watch their parent instead.
        this.targets = new Map();
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              (this.targets.get(entry.target) || [entry.target]).forEach((el) => el.classList.add('is-revealed'));
              this.targets.delete(entry.target);
              this.observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0 }
        );
      }

      elements.forEach((el) => {
        $$('[data-ik-stagger] > *', el).forEach((child, index) => child.style.setProperty('--ik-index', index));
        if (el.hasAttribute('data-ik-stagger')) {
          Array.from(el.children).forEach((child, index) => child.style.setProperty('--ik-index', index));
        }
        const target = el.dataset.ikReveal === 'clip' && el.parentElement ? el.parentElement : el;
        const group = this.targets.get(target) || [];
        if (!group.includes(el)) group.push(el);
        this.targets.set(target, group);
        this.observer.observe(target);
      });
    },
  };
  IK.reveal = reveal;

  /* ------------------------------------------------------------------ */
  /* Parallax + scroll scale (only runs while the element is on screen)  */
  /* ------------------------------------------------------------------ */
  const parallax = {
    active: new Set(),
    ticking: false,
    init(root = document) {
      if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
      const elements = $$('[data-ik-parallax]:not([data-ik-parallax-bound])', root);
      if (!elements.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.active.add(entry.target);
          else this.active.delete(entry.target);
        });
        this.request();
      });

      elements.forEach((el) => {
        el.setAttribute('data-ik-parallax-bound', '');
        observer.observe(el);
      });

      if (!this.bound) {
        this.bound = true;
        window.addEventListener('scroll', () => this.request(), { passive: true });
        window.addEventListener('resize', () => this.request(), { passive: true });
      }
    },
    request() {
      if (this.ticking || !this.active.size) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        const viewport = window.innerHeight;
        this.active.forEach((el) => {
          const desktopOnly = el.dataset.ikParallaxDesktop !== undefined;
          const rect = el.getBoundingClientRect();
          // -1 when the element enters from the bottom, 1 when it leaves at the top.
          const progress = Math.min(1, Math.max(-1, (viewport / 2 - (rect.top + rect.height / 2)) / viewport));
          const parsed = parseFloat(el.dataset.ikParallax);
          const speed = desktopOnly && window.innerWidth < 990 ? 0 : Number.isNaN(parsed) ? 0.1 : parsed;
          el.style.setProperty('--ik-parallax-y', `${(progress * speed * 100).toFixed(2)}px`);
          if (el.dataset.ikScrollScale !== undefined) {
            const scale = 1 + Math.max(0, progress) * (parseFloat(el.dataset.ikScrollScale) || 0.08);
            el.style.setProperty('--ik-scroll-scale', scale.toFixed(4));
          }
        });
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /* Background video: play only when visible, never with reduced motion */
  /* ------------------------------------------------------------------ */
  const videos = {
    init(root = document) {
      const list = $$('video[data-ik-autoplay]:not([data-ik-bound])', root);
      if (!list.length) return;
      list.forEach((video) => video.setAttribute('data-ik-bound', ''));
      if (reduceMotion.matches) {
        list.forEach((video) => video.removeAttribute('autoplay'));
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        });
      });
      list.forEach((video) => observer.observe(video));
    },
  };

  /* ------------------------------------------------------------------ */
  /* Marquee — pauses offscreen and via an accessible toggle             */
  /* ------------------------------------------------------------------ */
  const marquee = {
    init(root = document) {
      $$('[data-ik-marquee]:not([data-ik-bound])', root).forEach((el) => {
        el.setAttribute('data-ik-bound', '');
        const toggle = $('[data-ik-marquee-toggle]', el);
        if (toggle) {
          toggle.addEventListener('click', () => {
            const paused = el.classList.toggle('is-paused');
            toggle.setAttribute('aria-pressed', String(paused));
            toggle.setAttribute('aria-label', paused ? strings.play || 'Play' : strings.pause || 'Pause');
          });
        }
        if ('IntersectionObserver' in window) {
          new IntersectionObserver((entries) => {
            entries.forEach((entry) => el.classList.toggle('is-offscreen', !entry.isIntersecting));
          }).observe(el);
        }
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /* Carousels: arrows, mouse drag, progress                             */
  /* ------------------------------------------------------------------ */
  const carousel = {
    init(root = document) {
      $$('[data-ik-carousel]:not([data-ik-bound])', root).forEach((el) => this.bind(el));
    },
    bind(el) {
      el.setAttribute('data-ik-bound', '');
      const track = $('[data-ik-track]', el);
      if (!track) return;
      const prev = $('[data-ik-prev]', el);
      const next = $('[data-ik-next]', el);
      const progress = $('[data-ik-progress-bar]', el);

      const step = () => {
        const item = track.firstElementChild;
        if (!item) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      };

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        el.classList.toggle('is-scrollable', max > 2);
        if (progress) {
          const visible = track.clientWidth / track.scrollWidth;
          const ratio = max > 0 ? track.scrollLeft / max : 0;
          progress.style.setProperty('--ik-progress-size', Math.min(1, visible).toFixed(3));
          progress.style.setProperty('--ik-progress-offset', (ratio * (1 - visible)).toFixed(3));
        }
      };

      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();

      // Mouse drag (touch devices keep native swipe).
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = track.scrollLeft;
      });
      window.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 4) {
          moved = true;
          track.classList.add('is-dragging');
        }
        track.scrollLeft = startScroll - delta;
      });
      window.addEventListener('pointerup', () => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
      });
      // Swallow the click that ends a drag so cards don't navigate.
      track.addEventListener(
        'click',
        (event) => {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        },
        true
      );
      track.addEventListener('dragstart', (event) => event.preventDefault());
    },
  };

  /* ------------------------------------------------------------------ */
  /* Wishlist (client side)                                              */
  /* ------------------------------------------------------------------ */
  const WISHLIST_KEY = 'ironklad:wishlist';
  const wishlist = {
    items() {
      return storage.get(WISHLIST_KEY, []);
    },
    has(handle) {
      return this.items().includes(handle);
    },
    toggle(handle) {
      let items = this.items();
      const adding = !items.includes(handle);
      items = adding ? [handle, ...items].slice(0, 50) : items.filter((item) => item !== handle);
      storage.set(WISHLIST_KEY, items);
      this.sync();
      document.dispatchEvent(new CustomEvent('ironklad:wishlist', { detail: { handle, adding, items } }));
      return adding;
    },
    sync(root = document) {
      const items = this.items();
      $$('[data-ik-wishlist]', root).forEach((button) => {
        const active = items.includes(button.dataset.ikWishlist);
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-active', active);
      });
      $$('[data-ik-wishlist-count]').forEach((badge) => {
        badge.textContent = items.length;
        badge.hidden = items.length === 0;
      });
    },
    init() {
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ik-wishlist]');
        if (!button) return;
        event.preventDefault();
        const adding = this.toggle(button.dataset.ikWishlist);
        button.classList.remove('is-bouncing');
        void button.offsetWidth;
        button.classList.add('is-bouncing');
        IK.toast(adding ? strings.wishlistAdded : strings.wishlistRemoved, { timeout: 2000 });
      });
      this.sync();
    },
  };
  IK.wishlist = wishlist;

  /* ------------------------------------------------------------------ */
  /* Recently viewed                                                     */
  /* ------------------------------------------------------------------ */
  const RECENT_KEY = 'ironklad:recently-viewed';
  const recentlyViewed = {
    track() {
      const handle = config.product?.handle;
      if (!handle) return;
      const items = storage.get(RECENT_KEY, []).filter((item) => item !== handle);
      storage.set(RECENT_KEY, [handle, ...items].slice(0, 16));
    },
    items() {
      return storage.get(RECENT_KEY, []);
    },
  };
  IK.recentlyViewed = recentlyViewed;

  /* Product lists rendered from handles (recently viewed + wishlist). */
  const productLists = {
    cache: new Map(),
    fetchCard(handle) {
      if (!this.cache.has(handle)) {
        const root = (config.routes?.root || '/').replace(/\/?$/, '/');
        const url = `${root}products/${encodeURIComponent(handle)}?view=ik-card`;
        this.cache.set(
          handle,
          fetch(url)
            .then((response) => (response.ok ? response.text() : ''))
            .then((html) => html.trim())
            .catch(() => '')
        );
      }
      return this.cache.get(handle);
    },
    async render(container) {
      const source = container.dataset.ikProductList;
      const limit = parseInt(container.dataset.limit, 10) || 12;
      const exclude = config.product?.handle;
      let handles = source === 'wishlist' ? wishlist.items() : recentlyViewed.items();
      handles = handles.filter((handle) => handle !== exclude || source === 'wishlist').slice(0, limit);

      const section = container.closest('[data-ik-product-list-section]') || container;
      const target = $('[data-ik-product-list-items]', container) || container;
      const empty = $('[data-ik-product-list-empty]', section);

      if (!handles.length) {
        target.innerHTML = '';
        section.classList.add('is-empty');
        if (empty) empty.hidden = false;
        return;
      }

      target.setAttribute('aria-busy', 'true');
      const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
      const html = cards
        .filter(Boolean)
        .map((card) => `<li class="ik-carousel__item grid__item">${card}</li>`)
        .join('');
      target.innerHTML = html;
      target.removeAttribute('aria-busy');
      const hasItems = Boolean(html);
      section.classList.toggle('is-empty', !hasItems);
      if (empty) empty.hidden = hasItems;
      wishlist.sync(target);
      carousel.init(section);
      $$('[data-ik-carousel][data-ik-bound]', section).forEach((el) => $('[data-ik-track]', el)?.dispatchEvent(new Event('scroll')));
    },
    init(root = document) {
      $$('[data-ik-product-list]', root).forEach((container) => this.render(container));
    },
  };
  IK.productLists = productLists;
  document.addEventListener('ironklad:wishlist', () => {
    $$('[data-ik-product-list="wishlist"]').forEach((container) => productLists.render(container));
  });

  /* ------------------------------------------------------------------ */
  /* Card color swatches                                                 */
  /* ------------------------------------------------------------------ */
  const swatches = {
    apply(button) {
      const card = button.closest('.card-wrapper');
      if (!card) return;
      const image = $('.card__media img', card);
      if (image && button.dataset.src) {
        if (button.dataset.srcset) image.srcset = button.dataset.srcset;
        image.src = button.dataset.src;
      }
      $$('[data-ik-swatch]', card).forEach((swatch) => swatch.setAttribute('aria-pressed', String(swatch === button)));
      if (button.dataset.url) {
        $$('a[href].full-unstyled-link', card).forEach((link) => (link.href = button.dataset.url));
      }
    },
    init() {
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ik-swatch]');
        if (!button) return;
        event.preventDefault();
        this.apply(button);
      });
      document.addEventListener('pointerover', (event) => {
        if (event.pointerType !== 'mouse') return;
        const button = event.target.closest('[data-ik-swatch]');
        if (button) this.apply(button);
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /* Lookbook hotspots                                                   */
  /* ------------------------------------------------------------------ */
  const hotspots = {
    closeAll(except) {
      $$('[data-ik-hotspot][aria-expanded="true"]').forEach((button) => {
        if (button === except) return;
        button.setAttribute('aria-expanded', 'false');
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      });
    },
    init() {
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ik-hotspot]');
        if (button) {
          const open = button.getAttribute('aria-expanded') !== 'true';
          this.closeAll(button);
          button.setAttribute('aria-expanded', String(open));
          const panel = document.getElementById(button.getAttribute('aria-controls'));
          if (panel) panel.hidden = !open;
          return;
        }
        if (!event.target.closest('.ik-hotspot__card')) this.closeAll();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = $('[data-ik-hotspot][aria-expanded="true"]');
        if (open) {
          this.closeAll();
          open.focus();
        }
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /* Accordion groups: one open at a time on mobile                      */
  /* ------------------------------------------------------------------ */
  const accordions = {
    init() {
      document.addEventListener(
        'toggle',
        (event) => {
          const details = event.target;
          if (!(details instanceof HTMLDetailsElement) || !details.open) return;
          if (!details.parentElement?.matches('.product__accordion')) return;
          const group = details.closest('[data-ik-accordion-group], .product__info-container');
          if (!group || window.innerWidth >= 750) return;
          $$('.product__accordion > details[open]', group).forEach((other) => {
            if (other !== details) other.open = false;
          });
        },
        true
      );
    },
  };

  /* ------------------------------------------------------------------ */
  /* Dialogs (size guide)                                                */
  /* ------------------------------------------------------------------ */
  const dialogs = {
    init() {
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-ik-dialog-open]');
        if (opener) {
          const dialog = document.getElementById(opener.dataset.ikDialogOpen);
          if (dialog && typeof dialog.showModal === 'function') {
            event.preventDefault();
            dialog.showModal();
            document.body.classList.add('ik-dialog-open');
          }
          return;
        }
        const closer = event.target.closest('[data-ik-dialog-close]');
        if (closer) {
          closer.closest('dialog')?.close();
          return;
        }
        // Backdrop click: the event target is the dialog element itself.
        if (event.target instanceof HTMLDialogElement && event.target.matches('.ik-dialog')) {
          const rect = event.target.getBoundingClientRect();
          const inside =
            event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
          if (!inside) event.target.close();
        }
      });
      document.addEventListener(
        'close',
        (event) => {
          if (event.target instanceof HTMLDialogElement) document.body.classList.remove('ik-dialog-open');
        },
        true
      );

      // Size guide unit switch (inches <-> centimetres).
      document.addEventListener('change', (event) => {
        const input = event.target.closest('[data-ik-unit]');
        if (!input) return;
        const table = input.closest('.ik-size-guide')?.querySelector('[data-ik-size-table]');
        if (!table) return;
        const toCm = input.value === 'cm';
        $$('[data-in]', table).forEach((cell) => {
          const raw = cell.dataset.in;
          cell.textContent = toCm
            ? raw.replace(/\d+(\.\d+)?/g, (value) => String(Math.round(parseFloat(value) * 2.54)))
            : raw;
        });
        $$('[data-ik-unit-label]', table).forEach((label) => (label.textContent = toCm ? 'cm' : 'in'));
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /* Header scroll state                                                 */
  /* ------------------------------------------------------------------ */
  const header = {
    init() {
      const section = $('.section-header');
      if (!section) return;
      let ticking = false;
      let scrolled = null;
      const root = document.documentElement;
      const measure = () => {
        const sticky = $('sticky-header', section);
        const alwaysVisible = sticky && sticky.dataset.stickyType !== 'on-scroll-up';
        root.style.setProperty('--ik-header-offset', alwaysVisible ? `${section.offsetHeight}px` : '0px');
        root.style.setProperty('--ik-header-height', `${section.offsetHeight}px`);
      };
      const update = () => {
        ticking = false;
        const next = window.scrollY > 24;
        if (next !== scrolled) {
          scrolled = next;
          root.classList.toggle('ik-scrolled', next);
          window.setTimeout(measure, 400);
        }
      };
      window.addEventListener('resize', measure, { passive: true });
      measure();
      window.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        },
        { passive: true }
      );
      update();
    },
  };

  /* ------------------------------------------------------------------ */
  /* Custom cursor + magnetic buttons (desktop mouse only)               */
  /* ------------------------------------------------------------------ */
  const cursor = {
    init() {
      if (!config.cursor || !finePointer.matches || reduceMotion.matches) return;
      const el = document.createElement('div');
      el.className = 'ik-cursor';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = '<span class="ik-cursor__label"></span>';
      document.body.appendChild(el);
      const label = el.firstElementChild;
      let x = 0;
      let y = 0;
      let frame = null;

      const move = () => {
        frame = null;
        el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      };

      document.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          x = event.clientX;
          y = event.clientY;
          const zone = event.target.closest('[data-ik-cursor]');
          let show = false;
          if (zone) {
            const blocked = event.target.closest('button, input, select, textarea, summary, [role="button"], [data-ik-cursor-block]');
            const overLink = event.target.closest('a');
            if (!blocked && (!overLink || zone.hasAttribute('data-ik-cursor-links'))) {
              const area = zone.dataset.ikCursorArea ? $(zone.dataset.ikCursorArea, zone) : null;
              if (area) {
                const rect = area.getBoundingClientRect();
                show = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
              } else {
                show = true;
              }
            }
          }
          if (show) {
            label.textContent = zone.dataset.ikCursor;
            el.classList.add('is-visible');
          } else {
            el.classList.remove('is-visible');
          }
          if (!frame) frame = requestAnimationFrame(move);
        },
        { passive: true }
      );
      document.addEventListener('pointerleave', () => el.classList.remove('is-visible'));
    },
  };

  /* ------------------------------------------------------------------ */
  /* Tilt + pointer light: [data-ik-tilt="max degrees"]                  */
  /* Exposes --ik-mx / --ik-my (0–1 pointer position) for CSS.            */
  /* ------------------------------------------------------------------ */
  const tilt = {
    current: null,
    frame: null,
    x: 0,
    y: 0,
    reset(el) {
      el.classList.remove('is-tilting');
      el.style.setProperty('--ik-rx', '0deg');
      el.style.setProperty('--ik-ry', '0deg');
      el.style.setProperty('--ik-mx', '0.5');
      el.style.setProperty('--ik-my', '0.5');
    },
    apply() {
      this.frame = null;
      const el = this.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const mx = Math.min(1, Math.max(0, (this.x - rect.left) / rect.width));
      const my = Math.min(1, Math.max(0, (this.y - rect.top) / rect.height));
      const max = parseFloat(el.dataset.ikTilt) || 4;
      el.style.setProperty('--ik-mx', mx.toFixed(3));
      el.style.setProperty('--ik-my', my.toFixed(3));
      el.style.setProperty('--ik-ry', `${((mx - 0.5) * max * 2).toFixed(2)}deg`);
      el.style.setProperty('--ik-rx', `${((0.5 - my) * max * 2).toFixed(2)}deg`);
    },
    init() {
      if (!finePointer.matches || reduceMotion.matches) return;
      document.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          const el = event.target.closest('[data-ik-tilt]');
          if (el !== this.current) {
            if (this.current) this.reset(this.current);
            this.current = el;
            if (el) el.classList.add('is-tilting');
          }
          if (!el) return;
          this.x = event.clientX;
          this.y = event.clientY;
          if (!this.frame) this.frame = requestAnimationFrame(() => this.apply());
        },
        { passive: true }
      );
      document.documentElement.addEventListener('pointerleave', () => {
        if (this.current) this.reset(this.current);
        this.current = null;
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /* Product card image scrubbing: move across the image to flip photos  */
  /* ------------------------------------------------------------------ */
  const scrub = {
    current: null,
    hydrate(media) {
      if (media.dataset.ikHydrated) return;
      media.dataset.ikHydrated = 'true';
      $$('img[data-src]', media).forEach((img) => {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
      });
    },
    show(media, index) {
      const frames = $$(':scope > img', media);
      frames.forEach((frame, i) => frame.classList.toggle('is-active', i === index));
      $$('.ik-card-bars > span', media).forEach((bar, i) => bar.classList.toggle('is-active', i === index));
    },
    stop(media) {
      media.classList.remove('is-scrubbing');
      $$(':scope > img', media).forEach((frame) => frame.classList.remove('is-active'));
      this.show(media, -1);
      $('.ik-card-bars > span', media)?.classList.add('is-active');
    },
    init() {
      if (!finePointer.matches) return;
      document.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          const card = event.target.closest('.card-wrapper');
          const media = card ? $('[data-ik-scrub]', card) : null;
          let inside = false;
          let rect = null;
          if (media) {
            rect = media.getBoundingClientRect();
            inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
          }
          const target = inside ? media : null;
          if (target !== this.current) {
            if (this.current) this.stop(this.current);
            this.current = target;
            if (target) {
              this.hydrate(target);
              target.classList.add('is-scrubbing');
            }
          }
          if (!target) return;
          const frames = $$(':scope > img', target).filter((img) => img.getAttribute('src'));
          const count = frames.length;
          if (count < 2) return;
          const index = Math.min(count - 1, Math.floor(((event.clientX - rect.left) / rect.width) * count));
          const all = $$(':scope > img', target);
          this.show(target, all.indexOf(frames[index]));
        },
        { passive: true }
      );
    },
  };

  const magnetic = {
    init() {
      if (!config.magnetic || !finePointer.matches || reduceMotion.matches) return;
      document.addEventListener(
        'pointermove',
        (event) => {
          const target = event.target.closest('[data-ik-magnetic]');
          if (this.current && this.current !== target) {
            this.current.style.removeProperty('--ik-magnet-x');
            this.current.style.removeProperty('--ik-magnet-y');
            this.current = null;
          }
          if (!target) return;
          this.current = target;
          const rect = target.getBoundingClientRect();
          const dx = ((event.clientX - rect.left) / rect.width - 0.5) * 8;
          const dy = ((event.clientY - rect.top) / rect.height - 0.5) * 6;
          target.style.setProperty('--ik-magnet-x', `${dx.toFixed(1)}px`);
          target.style.setProperty('--ik-magnet-y', `${dy.toFixed(1)}px`);
        },
        { passive: true }
      );
    },
  };

  /* ------------------------------------------------------------------ */
  /* Cart helpers: refresh, add, discount, progress animation            */
  /* ------------------------------------------------------------------ */
  const cart = {
    lastProgress: null,

    sectionTargets() {
      const targets = [];
      const drawer = $('cart-drawer');
      if (drawer) targets.push({ section: 'cart-drawer', apply: (html) => this.swap(html, '.drawer__inner', $('#CartDrawer .drawer__inner')) });
      const items = $('#main-cart-items');
      if (items?.dataset.id) targets.push({ section: items.dataset.id, apply: (html) => this.swap(html, '.js-contents', $('.js-contents', items)) });
      const footer = $('#main-cart-footer');
      if (footer?.dataset.id) targets.push({ section: footer.dataset.id, apply: (html) => this.swap(html, '.js-contents', $('.js-contents', footer)) });
      if (document.getElementById('cart-icon-bubble')) {
        targets.push({ section: 'cart-icon-bubble', apply: (html) => this.swap(html, '.shopify-section', $('#cart-icon-bubble')) });
      }
      return targets;
    },

    swap(html, selector, target) {
      if (!html || !target) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (source) target.innerHTML = source.innerHTML;
    },

    async refresh(cartData, source = 'ironklad') {
      const targets = this.sectionTargets();
      if (targets.length) {
        const ids = targets.map((target) => target.section).join(',');
        const response = await fetch(`${window.location.pathname}?sections=${ids}`);
        if (response.ok) {
          const sections = await response.json();
          targets.forEach((target) => target.apply(sections[target.section]));
        }
      }
      const data = cartData || (await fetch(`${config.routes?.cart || '/cart'}.js`).then((r) => r.json()).catch(() => null));
      const drawer = $('cart-drawer');
      if (drawer && data) {
        const empty = data.item_count === 0;
        drawer.classList.toggle('is-empty', empty);
        $('.drawer__inner', drawer)?.classList.toggle('is-empty', empty);
      }
      emitCartUpdate(data, source);
      cart.afterRender();
      return data;
    },

    setBusy(button, busy) {
      if (!button) return;
      button.classList.toggle('loading', busy);
      button.setAttribute('aria-busy', String(busy));
      button.disabled = busy;
    },

    async add(variantId, button) {
      this.setBusy(button, true);
      try {
        const response = await fetch(`${config.routes?.cartAdd || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
        await this.refresh(null, 'ironklad-add');
        IK.toast(strings.added);
      } catch (error) {
        IK.toast(error.message || strings.cartError, { type: 'error' });
      } finally {
        this.setBusy(button, false);
      }
    },

    async applyDiscount(form) {
      const input = $('input[name="discount"]', form);
      const button = $('button[type="submit"]', form);
      const code = input?.value.trim();
      if (!code) return;
      const existing = (form.dataset.codes || '').split(',').filter(Boolean);
      await this.updateDiscounts([...new Set([...existing, code])], button, code);
    },

    async updateDiscounts(codes, button, attempted) {
      this.setBusy(button, true);
      try {
        const response = await fetch(`${config.routes?.cartUpdate || '/cart/update'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || strings.cartError);
        if (attempted) {
          const match = (data.discount_codes || []).find((d) => d.code.toLowerCase() === attempted.toLowerCase());
          if (!match || !match.applicable) IK.toast(strings.discountInvalid, { type: 'error' });
          else IK.toast(strings.discountApplied);
        }
        await this.refresh(data, 'ironklad-discount');
      } catch (error) {
        IK.toast(error.message || strings.cartError, { type: 'error' });
      } finally {
        this.setBusy(button, false);
      }
    },

    afterRender() {
      $$('[data-ik-shipping-progress]').forEach((bar) => {
        const value = parseFloat(bar.dataset.value) || 0;
        const from = this.lastProgress === null ? 0 : this.lastProgress;
        bar.style.setProperty('--ik-progress', from);
        requestAnimationFrame(() => requestAnimationFrame(() => bar.style.setProperty('--ik-progress', value)));
        this.lastProgress = value;
      });
    },

    init() {
      document.addEventListener('click', (event) => {
        const add = event.target.closest('[data-ik-add-variant]');
        if (add) {
          event.preventDefault();
          this.add(add.dataset.ikAddVariant, add);
          return;
        }
        const remove = event.target.closest('[data-ik-remove-discount]');
        if (remove) {
          event.preventDefault();
          const form = remove.closest('[data-ik-discount-wrapper]')?.querySelector('[data-ik-discount]');
          const codes = (form?.dataset.codes || '').split(',').filter((code) => code && code !== remove.dataset.ikRemoveDiscount);
          this.updateDiscounts(codes, remove);
        }
      });
      document.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-ik-discount]');
        if (!form) return;
        event.preventDefault();
        this.applyDiscount(form);
      });

      // Checkout loading state.
      document.addEventListener('click', (event) => {
        const checkout = event.target.closest('#CartDrawer-Checkout, #checkout');
        if (checkout && !checkout.disabled) checkout.classList.add('ik-is-loading');
      });

      // Remember which add-to-cart button was used so it can confirm.
      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (form instanceof HTMLFormElement && form.matches('[data-type="add-to-cart-form"]')) {
            this.lastAddButton = event.submitter || $('[type="submit"]', form);
          }
        },
        true
      );

      onPubSub('cartUpdate', (event) => {
        const bubble = $('#cart-icon-bubble');
        if (bubble) {
          bubble.classList.remove('ik-pulse');
          void bubble.offsetWidth;
          bubble.classList.add('ik-pulse');
        }
        const count = event?.cartData?.item_count;
        if (typeof count === 'number') $$('[data-ik-cart-count]').forEach((el) => (el.textContent = count));

        if (event?.source === 'product-form') {
          const button = this.lastAddButton;
          if (button) {
            button.classList.add('ik-added');
            window.setTimeout(() => button.classList.remove('ik-added'), 1800);
          }
          if (!$('cart-drawer') || config.cartType !== 'drawer') IK.toast(strings.added);
        }
        window.setTimeout(() => this.afterRender(), 60);
      });

      this.afterRender();

      // The drawer re-renders its own markup after quantity changes.
      const drawer = $('#CartDrawer');
      if (drawer && 'MutationObserver' in window) {
        let pending = false;
        new MutationObserver(() => {
          if (pending) return;
          pending = true;
          requestAnimationFrame(() => {
            pending = false;
            this.afterRender();
          });
        }).observe(drawer, { childList: true, subtree: true });
      }
    },
  };
  IK.cart = cart;

  /* ------------------------------------------------------------------ */
  /* Sticky add to cart (mirrors the real product form)                  */
  /* ------------------------------------------------------------------ */
  const stickyAtc = {
    init() {
      const bar = $('[data-ik-sticky-atc]');
      if (!bar) return;
      // Rise can re-render the button and price on variant change, so look
      // them up fresh every time instead of holding node references.
      const getMainButton = () => $('main [id^="ProductSubmitButton-"]');
      const initialButton = getMainButton();
      if (!initialButton) return;
      const anchor = initialButton.closest('product-form') || initialButton;
      const info = initialButton.closest('product-info, .product') || document.body;
      const button = $('[data-ik-sticky-button]', bar);
      const label = $('[data-ik-sticky-label]', bar);
      const price = $('[data-ik-sticky-price]', bar);

      let frame = null;
      const mirror = () => {
        frame = null;
        const mainButton = getMainButton();
        if (!mainButton) return;
        button.disabled = mainButton.disabled;
        const text = mainButton.querySelector('span')?.textContent.trim();
        if (text && label) label.textContent = text;
        const priceSource = $('[id^="price-"]', info);
        if (price && priceSource) price.innerHTML = priceSource.innerHTML;
      };
      mirror();
      if ('MutationObserver' in window) {
        new MutationObserver(() => {
          if (!frame) frame = requestAnimationFrame(mirror);
        }).observe(info, { attributes: true, attributeFilter: ['disabled', 'class'], childList: true, subtree: true });
      }

      button.addEventListener('click', () => {
        const mainButton = getMainButton();
        if (!mainButton) return;
        if (mainButton.disabled) {
          mainButton.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
          return;
        }
        mainButton.click();
      });

      new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', passed);
        bar.setAttribute('aria-hidden', String(!passed));
        button.tabIndex = passed ? 0 : -1;
        document.documentElement.style.setProperty('--ik-sticky-offset', passed ? `${bar.offsetHeight}px` : '0px');
      }).observe(anchor);
    },
  };

  /* ------------------------------------------------------------------ */
  /* Collection: view switcher + progressive loading                     */
  /* ------------------------------------------------------------------ */
  const GRID_KEY = 'ironklad:grid-columns';
  const collection = {
    applyColumns(columns) {
      const grid = $('#product-grid.product-grid');
      if (!grid || !columns) return;
      grid.classList.forEach((cls) => {
        if (/^grid--\d-col-desktop$/.test(cls)) grid.classList.remove(cls);
      });
      grid.classList.add(`grid--${columns}-col-desktop`);
      grid.dataset.ikColumns = columns;
      $$('[data-ik-grid-cols]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.ikGridCols === String(columns))));
    },

    async loadMore(trigger) {
      if (trigger.dataset.loading === 'true') return;
      const url = trigger.dataset.nextUrl;
      const sectionId = trigger.dataset.sectionId;
      if (!url || !sectionId) return;
      trigger.dataset.loading = 'true';
      trigger.classList.add('loading');
      try {
        const separator = url.includes('?') ? '&' : '?';
        const response = await fetch(`${url}${separator}section_id=${sectionId}`);
        if (!response.ok) throw new Error(strings.loadError);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = $('#product-grid');
        const items = $$('#product-grid > .grid__item', html);
        items.forEach((item) => {
          item.setAttribute('data-ik-reveal', 'fade-up');
          grid.appendChild(item);
        });
        reveal.init(grid);
        wishlist.sync(grid);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(grid);

        const nextPager = $('[data-ik-load-more-wrapper]', html);
        const currentPager = $('[data-ik-load-more-wrapper]');
        if (currentPager && nextPager) currentPager.replaceWith(nextPager);
        else currentPager?.remove();
        this.bindInfinite();
      } catch (error) {
        IK.toast(error.message || strings.loadError, { type: 'error' });
        trigger.dataset.loading = 'false';
        trigger.classList.remove('loading');
      }
    },

    bindInfinite() {
      const trigger = $('[data-ik-load-more][data-infinite]');
      if (!trigger || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            this.loadMore(trigger);
          }
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(trigger);
    },

    init() {
      document.addEventListener('click', (event) => {
        const switcher = event.target.closest('[data-ik-grid-cols]');
        if (switcher) {
          storage.set(GRID_KEY, switcher.dataset.ikGridCols);
          this.applyColumns(switcher.dataset.ikGridCols);
          return;
        }
        const more = event.target.closest('[data-ik-load-more]');
        if (more) {
          event.preventDefault();
          this.loadMore(more);
        }
      });

      const container = $('#ProductGridContainer');
      if (!container) return;
      const saved = storage.get(GRID_KEY, null);
      if (saved) this.applyColumns(saved);
      this.bindInfinite();

      // Facets re-render the grid; restore state afterwards.
      new MutationObserver(() => {
        const columns = storage.get(GRID_KEY, null);
        if (columns && $('#product-grid')?.dataset.ikColumns !== columns) this.applyColumns(columns);
        wishlist.sync(container);
        reveal.init(container);
        this.bindInfinite();
      }).observe(container, { childList: true });
    },
  };

  /* ------------------------------------------------------------------ */
  /* Popular searches — fill the predictive search input                 */
  /* ------------------------------------------------------------------ */
  const search = {
    init() {
      document.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-ik-search-term]');
        if (!chip) return;
        const input = chip.closest('.search-modal__content, details-modal, form')?.querySelector('input[type="search"]');
        if (!input) return;
        event.preventDefault();
        input.value = chip.dataset.ikSearchTerm;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */
  const initSections = (root = document) => {
    reveal.init(root);
    parallax.init(root);
    videos.init(root);
    marquee.init(root);
    carousel.init(root);
    productLists.init(root);
    wishlist.sync(root);
  };
  IK.initSections = initSections;

  // Content injected later (product recommendations) needs the same wiring.
  const lateContent = {
    init() {
      if (!('MutationObserver' in window)) return;
      $$('product-recommendations, [data-ik-late-content]').forEach((el) => {
        new MutationObserver(() => initSections(el)).observe(el, { childList: true });
      });
    },
  };

  const boot = () => {
    recentlyViewed.track();
    wishlist.init();
    swatches.init();
    hotspots.init();
    accordions.init();
    dialogs.init();
    header.init();
    cursor.init();
    tilt.init();
    scrub.init();
    magnetic.init();
    cart.init();
    stickyAtc.init();
    collection.init();
    search.init();
    lateContent.init();
    initSections();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', (event) => initSections(event.target));
    }
    IK.ready = true;
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
